import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Car, User, CreditCard, Check, Loader2, ShieldCheck, ArrowLeft, CheckCircle2, Banknote, ChevronUp } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons, FUNDING } from "@paypal/react-paypal-js";
import { prestationGroups, optionItems, findPrestation, findOption, formatEuro, priceLabel, BUNDLE_PARTS, matchesFuel } from "@/data/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-[#454C50] focus:border-[#6EB000] outline-none px-4 py-3 text-sm text-white placeholder:text-white/30 transition-colors duration-300";

const CARBURANTS = [
  { id: "Essence", color: "#6EB000" },
  { id: "Diesel", color: "#B45309" },
  { id: "Hybride", color: "#4DA3FF" },
  { id: "Autre", color: "#9AA09D" },
];

const emptyVehicle = { marque: "", modele: "", annee: "", motorisation: "", carburant: "Essence", puissance: "", immatriculation: "" };
const emptyCustomer = { nom: "", prenom: "", telephone: "", email: "", adresse: "", code_postal: "", ville: "" };

const SectionTitle = ({ num, title, icon: Icon }) => (
  <div className="flex items-center gap-4 mb-6">
    <span className="font-display text-4xl text-outline leading-none">{num}</span>
    <Icon className="w-6 h-6 text-[#6EB000]" strokeWidth={1.6} />
    <h2 className="font-display text-2xl sm:text-3xl text-white">{title}</h2>
  </div>
);

export default function Configurator() {
  const [searchParams] = useSearchParams();
  const [vehicle, setVehicle] = useState(emptyVehicle);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [prestationId, setPrestationId] = useState("");
  const [optIds, setOptIds] = useState([]);
  const [cgv, setCgv] = useState(false);
  const [step, setStep] = useState("form");
  const [order, setOrder] = useState(null);
  const [payMethod, setPayMethod] = useState("paypal");
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Configurateur & devis — NBPerf";
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
    const p = searchParams.get("prestation");
    const o = searchParams.get("option");
    if (p && findPrestation(p)) setPrestationId(p);
    if (o && findOption(o)) setOptIds([o]);
  }, [searchParams]);

  const prestation = prestationId ? findPrestation(prestationId) : null;
  const opts = optIds.map(findOption).filter(Boolean);
  const optsTotal = opts.reduce((s, o) => s + (o.price || 0), 0);
  const total = prestation && prestation.price !== null ? prestation.price + optsTotal : null;
  const needsConfirm = prestation?.confirm;

  // Filtrage par carburant : seules les prestations/options compatibles sont affichées
  const visibleGroups = prestationGroups
    .map((g) => ({ ...g, items: g.items.filter((p) => matchesFuel(p, vehicle.carburant)) }))
    .filter((g) => g.items.length > 0);
  const visibleOptions = optionItems.filter((o) => matchesFuel(o, vehicle.carburant));
  const carburantColor = CARBURANTS.find((c) => c.id === vehicle.carburant)?.color || "#6EB000";

  useEffect(() => {
    if (prestationId && !matchesFuel(findPrestation(prestationId), vehicle.carburant)) setPrestationId("");
    setOptIds((prev) => prev.filter((id) => matchesFuel(findOption(id), vehicle.carburant)));
  }, [vehicle.carburant]);

  const toggleOpt = (id) => {
    setOptIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      let next = [...prev, id];
      if (BUNDLE_PARTS[id]) next = next.filter((x) => !BUNDLE_PARTS[id].includes(x));
      next = next.filter((x) => !(BUNDLE_PARTS[x] || []).includes(id));
      return next;
    });
  };

  const setV = (k) => (e) => setVehicle({ ...vehicle, [k]: e.target.value });
  const setC = (k) => (e) => setCustomer({ ...customer, [k]: e.target.value });

  const valid =
    vehicle.marque && vehicle.modele && vehicle.annee && vehicle.motorisation && vehicle.puissance &&
    customer.nom && customer.prenom && customer.telephone && customer.email &&
    customer.adresse && customer.code_postal && customer.ville && prestationId && cgv;

  const submit = async () => {
    if (!valid) {
      toast.error("Complétez tous les champs obligatoires et acceptez les CGV.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/orders`, {
        prestation_id: prestationId,
        option_ids: optIds,
        vehicle,
        customer,
        cgv,
      });
      setOrder(r.data);
      setStep(r.data.status === "quote_requested" ? "done" : "payment");
      window.scrollTo(0, 0);
    } catch {
      toast.error("Erreur lors de l'enregistrement. Réessayez ou appelez le 07 81 90 12 48.");
    } finally {
      setBusy(false);
    }
  };

  const paypalCreate = async () => {
    const r = await axios.post(`${API}/payments/paypal/create`, { order_id: order.order_id });
    return r.data.paypal_order_id;
  };

  const paypalOnPaid = (ref) => {
    setOrder({ ...order, status: "paid", payment: { method: "paypal", ref } });
    setStep("done");
    window.scrollTo(0, 0);
  };

  const paypalApprove = async (data) => {
    try {
      const r = await axios.post(`${API}/payments/paypal/capture`, { paypal_order_id: data.orderID });
      if (r.data.status === "paid") paypalOnPaid(r.data.payment_ref);
      else toast.error("Paiement PayPal non confirmé.");
    } catch {
      toast.error("Erreur lors du paiement PayPal.");
    }
  };

  const confirmCash = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/orders/${order.order_id}/cash`);
      setOrder(r.data);
      setStep("done");
      window.scrollTo(0, 0);
    } catch {
      toast.error("Erreur. Réessayez ou appelez le 07 81 90 12 48.");
      setBusy(false);
    }
  };

  if (step === "done" && order) {
    const isCash = order.status === "pay_on_site";
    return (
      <main className="pt-16 min-h-screen" data-testid="configurator-done">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-20 text-center">
          <CheckCircle2 className="w-16 h-16 text-[#6EB000] mx-auto" strokeWidth={1.4} />
          <h1 className="font-display text-4xl sm:text-6xl text-white mt-8" data-testid="done-title">
            {order.status === "paid" ? "Paiement confirmé" : "Demande enregistrée"}
          </h1>
          <p className="text-white/60 mt-4">
            {order.status === "paid"
              ? "Merci ! Votre paiement est confirmé et votre demande est enregistrée. Nicolas vous recontacte pour planifier l'intervention."
              : isCash
                ? "Votre demande est enregistrée. Vous réglerez la prestation en espèces lors de votre rendez-vous NBPerf."
                : "Votre demande est enregistrée. Le tarif exact vous sera confirmé par NBPerf avant tout paiement."}
          </p>
          <div className="mt-10 bg-[#343A3D] border border-[#454C50] p-6 text-left space-y-2" data-testid="order-confirmation">
            <p className="text-[11px] uppercase tracking-widest text-white/40">Référence</p>
            <p className="font-mono text-[#6EB000] text-sm" data-testid="order-ref">{order.order_id}</p>
            {order.amount_eur != null && (
              <p className="font-display text-3xl text-white mt-2">{formatEuro(order.amount_eur)}</p>
            )}
            {isCash && (
              <div className="mt-3 border border-[#F5A623]/40 bg-[#F5A623]/5 p-4 text-sm" data-testid="done-cash-note">
                <p><span className="text-white/50">Mode de paiement :</span> <span className="text-[#F5A623] font-bold">Espèces – paiement sur place</span></p>
                <p className="mt-1"><span className="text-white/50">Statut :</span> <span className="text-white">À régler lors du rendez-vous</span></p>
                <p className="text-white/70 mt-2">Merci de prévoir le règlement lors de votre rendez-vous.</p>
              </div>
            )}
            {order.payment?.ref && (
              <p className="text-xs text-white/40 mt-1">Réf. paiement : {order.payment.ref}</p>
            )}
          </div>
          <Link to="/" className="mt-10 inline-flex items-center gap-2 text-[#6EB000] text-sm uppercase tracking-widest hover:underline" data-testid="done-back-home">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>
        </div>
      </main>
    );
  }

  if (step === "payment" && order) {
    const methodBtn = (active) =>
      `text-left border p-5 transition-colors duration-300 ${
        active ? "border-[#6EB000] bg-[#6EB000]/10" : "border-[#454C50] hover:border-[#6EB000]/50"
      }`;
    return (
      <main className="pt-16 min-h-screen" data-testid="payment-step">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <p className="font-script text-2xl text-[#6EB000] mb-2">Dernière étape</p>
          <h1 className="font-display text-4xl sm:text-6xl text-white" data-testid="payment-title">Choisissez votre paiement</h1>

          <div className="mt-10 bg-[#343A3D] border border-[#6EB000]/40 glow-green p-6 sm:p-8" data-testid="payment-recap">
            <div className="flex justify-between text-sm text-white/70">
              <span>{order.prestation.label}</span>
              <span>{order.prestation.price_label}</span>
            </div>
            {order.options.map((o) => (
              <div key={o.id} className="flex justify-between text-sm text-white/50 mt-2">
                <span>+ {o.label}</span>
                <span>{o.price_label}</span>
              </div>
            ))}
            <div className="border-t border-[#454C50] mt-4 pt-4 flex justify-between items-center">
              <span className="uppercase tracking-widest text-xs text-white/50">Total</span>
              <span className="font-display text-4xl text-[#6EB000]" data-testid="payment-total">{formatEuro(order.amount_eur)}</span>
            </div>
            <p className="text-xs text-white/40 mt-3">
              {order.vehicle.marque} {order.vehicle.modele} ({order.vehicle.annee}) — {customer.prenom} {customer.nom}
            </p>
          </div>

          <div className="mt-8 space-y-5">
            <div className="grid sm:grid-cols-3 gap-4" data-testid="payment-methods">
              <button type="button" onClick={() => setPayMethod("paypal")} className={methodBtn(payMethod === "paypal")} data-testid="pay-method-paypal">
                <CreditCard className="w-6 h-6 text-[#6EB000] mb-3" strokeWidth={1.6} />
                <p className="text-white font-bold text-sm">PayPal</p>
                <p className="text-white/40 text-xs mt-1">Compte PayPal ou Pay Later si éligible</p>
              </button>
              <button type="button" onClick={() => setPayMethod("card")} className={methodBtn(payMethod === "card")} data-testid="pay-method-card">
                <ShieldCheck className="w-6 h-6 text-[#6EB000] mb-3" strokeWidth={1.6} />
                <p className="text-white font-bold text-sm">Carte bancaire</p>
                <p className="text-white/40 text-xs mt-1">Via PayPal Checkout, sécurisé</p>
              </button>
              <button type="button" onClick={() => setPayMethod("cash")} className={methodBtn(payMethod === "cash")} data-testid="pay-method-cash">
                <Banknote className="w-6 h-6 text-[#6EB000] mb-3" strokeWidth={1.6} />
                <p className="text-white font-bold text-sm">Espèces — paiement sur place</p>
                <p className="text-white/40 text-xs mt-1">Vous réglez lors du rendez-vous NBPerf</p>
              </button>
            </div>

            {payMethod === "paypal" && (
              <div className="border border-[#454C50] p-5" data-testid="pay-paypal-area">
                {config?.paypalConfigured ? (
                  <PayPalScriptProvider options={{ "client-id": config.paypalClientId, currency: "EUR", "enable-funding": "paylater,card" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "black", shape: "rect", label: "paypal" }}
                      forceReRender={[order.amount_eur]}
                      createOrder={paypalCreate}
                      onApprove={paypalApprove}
                      onError={() => toast.error("Paiement PayPal indisponible pour le moment.")}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <p className="text-sm text-white/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement de PayPal…</p>
                )}
                <p className="text-xs text-white/40 mt-4" data-testid="paylater-note">
                  Paiement en plusieurs fois disponible via PayPal, sous réserve d'éligibilité — PayPal affiche les options disponibles au moment du paiement.
                </p>
              </div>
            )}

            {payMethod === "card" && (
              <div className="border border-[#454C50] p-5" data-testid="pay-card-area">
                {config?.paypalConfigured ? (
                  <PayPalScriptProvider options={{ "client-id": config.paypalClientId, currency: "EUR", "enable-funding": "card" }}>
                    <PayPalButtons
                      fundingSource={FUNDING.CARD}
                      style={{ layout: "vertical", color: "black", shape: "rect", label: "pay" }}
                      forceReRender={[order.amount_eur]}
                      createOrder={paypalCreate}
                      onApprove={paypalApprove}
                      onError={() => toast.error("Paiement par carte indisponible pour le moment.")}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <p className="text-sm text-white/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement du paiement par carte…</p>
                )}
                <p className="flex items-center gap-2 text-xs text-white/40 mt-4">
                  <ShieldCheck className="w-4 h-4 text-[#6EB000]" /> Paiement sécurisé par PayPal — vos données bancaires ne transitent jamais par ce site
                </p>
              </div>
            )}

            {payMethod === "cash" && (
              <>
                <p className="text-sm text-white/70 border border-[#F5A623]/40 bg-[#F5A623]/5 p-4" data-testid="cash-warning">
                  Vous réglerez la prestation en espèces lors de votre rendez-vous NBPerf. Aucun paiement ne sera prélevé en ligne.
                </p>
                <button
                  onClick={confirmCash}
                  disabled={busy}
                  className="clip-angle w-full inline-flex items-center justify-center gap-3 bg-[#6EB000] text-black font-bold px-8 py-5 text-sm uppercase tracking-widest hover:bg-[#5A9000] active:scale-95 transition-all duration-200 disabled:opacity-50"
                  data-testid="pay-cash-button"
                >
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Banknote className="w-5 h-5" />}
                  Confirmer — paiement sur place ({formatEuro(order.amount_eur)})
                </button>
              </>
            )}

            <ul className="grid sm:grid-cols-2 gap-2 text-xs text-white/60 border border-[#454C50] p-4" data-testid="payment-trust">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#6EB000] shrink-0 mt-0.5" /> Paiement sécurisé par PayPal</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#6EB000] shrink-0 mt-0.5" /> Carte bancaire acceptée</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#6EB000] shrink-0 mt-0.5" /> Paiement en plusieurs fois disponible via PayPal, sous réserve d'éligibilité</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#6EB000] shrink-0 mt-0.5" /> Paiement en espèces possible sur place</li>
            </ul>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-16" data-testid="configurator-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-32 sm:pt-24 sm:pb-24">
        <p className="font-script text-2xl text-[#6EB000] mb-2">Tarifs officiels NBPerf, prix en direct</p>
        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl text-white" data-testid="configurator-title">
          Préparez votre <span className="text-[#6EB000]">prestation</span>
        </h1>

        <div className="grid lg:grid-cols-3 gap-10 mt-14">
          <div className="lg:col-span-2 space-y-14">
            <section data-testid="cfg-vehicle-section">
              <SectionTitle num="01" title="Votre véhicule" icon={Car} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Marque * (ex : Volkswagen)" value={vehicle.marque} onChange={setV("marque")} className={inputCls} data-testid="cfg-marque" />
                <input required placeholder="Modèle * (ex : Golf 7)" value={vehicle.modele} onChange={setV("modele")} className={inputCls} data-testid="cfg-modele" />
                <input required placeholder="Année * (ex : 2018)" value={vehicle.annee} onChange={setV("annee")} className={inputCls} data-testid="cfg-annee" />
                <input required placeholder="Motorisation * (ex : 1.4 TSI)" value={vehicle.motorisation} onChange={setV("motorisation")} className={inputCls} data-testid="cfg-motorisation" />
                <div className="sm:col-span-2" data-testid="cfg-carburant">
                  <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Carburant *</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {CARBURANTS.map((c) => {
                      const active = vehicle.carburant === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setVehicle({ ...vehicle, carburant: c.id })}
                          className="border-2 px-4 py-3 text-sm uppercase tracking-widest font-bold transition-all duration-200"
                          style={{
                            borderColor: c.color,
                            backgroundColor: active ? c.color : "transparent",
                            color: active ? "#101214" : c.color,
                          }}
                          data-testid={`cfg-carburant-${c.id.toLowerCase()}`}
                        >
                          {c.id}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <input required placeholder="Puissance * (ex : 125 ch)" value={vehicle.puissance} onChange={setV("puissance")} className={inputCls} data-testid="cfg-puissance" />
                <input placeholder="Immatriculation (optionnel)" value={vehicle.immatriculation} onChange={setV("immatriculation")} className={inputCls} data-testid="cfg-immatriculation" />
              </div>
            </section>

            <section data-testid="cfg-prestation-section">
              <SectionTitle num="02" title="Prestation principale" icon={Check} />
              <p className="text-xs text-white/40 -mt-3 mb-6" data-testid="cfg-fuel-hint">
                Prestations compatibles avec votre carburant :{" "}
                <span className="font-bold uppercase tracking-widest" style={{ color: carburantColor }}>{vehicle.carburant}</span>
              </p>
              <div className="space-y-8">
                {visibleGroups.map((g) => (
                  <div key={g.title}>
                    <p className="text-[11px] uppercase tracking-widest text-white/40 mb-3">{g.title}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {g.items.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPrestationId(p.id)}
                          data-testid={`cfg-prestation-${p.id}`}
                          className={`text-left border transition-colors duration-300 ${
                            p.pack
                              ? prestationId === p.id
                                ? "border-[#E10600] bg-[#9EA4A1] ring-2 ring-[#6EB000] overflow-hidden"
                                : "border-[#E10600] bg-[#9EA4A1] hover:bg-[#A9AFAB] overflow-hidden"
                              : prestationId === p.id
                                ? "border-[#6EB000] bg-[#6EB000]/10 p-4"
                                : "border-[#454C50] hover:border-[#6EB000]/50 p-4"
                          }`}
                        >
                          {p.pack && (
                            <img
                              src="/pack-engine.png"
                              alt="Moteur préparé NBPerf — turbo, admission et échappement performance"
                              className="w-full h-20 sm:h-24 object-cover object-center"
                              loading="lazy"
                              data-testid={`cfg-pack-photo-${p.id}`}
                            />
                          )}
                          <div className={`flex justify-between items-start gap-3 ${p.pack ? "p-4" : ""}`}>
                            <div>
                              <p className={`font-bold text-sm ${p.pack ? "text-[#272B2E]" : "text-white"}`}>{p.label}</p>
                              {p.sub && <p className={`text-xs mt-1 ${p.pack ? "text-[#272B2E]/70" : "text-white/40"}`}>{p.sub}</p>}
                              {p.mention && <p className="text-[11px] italic text-[#8C1D18] mt-2">{p.mention}</p>}
                            </div>
                            <span className={`font-display text-lg whitespace-nowrap ${p.pack ? "text-[#272B2E]" : "text-[#6EB000]"}`}>{priceLabel(p)}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    {g.footerNote && g.items.some((p) => p.pack) && (
                      <p className="text-white/50 text-xs italic mt-3" data-testid="pack-footer-note">{g.footerNote}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section data-testid="cfg-options-section">
              <SectionTitle num="03" title="Options cumulables" icon={Check} />
              <div className="grid sm:grid-cols-2 gap-3">
                {visibleOptions.map((o) => (
                  <label
                    key={o.id}
                    data-testid={`cfg-option-${o.id}`}
                    className={`flex items-center justify-between gap-3 border p-4 cursor-pointer transition-colors duration-300 ${
                      optIds.includes(o.id) ? "border-[#F5A623] bg-[#F5A623]/10" : "border-[#454C50] hover:border-[#F5A623]/50"
                    }`}
                  >
                    <span className="flex items-center gap-3 text-sm text-white/80">
                      <input type="checkbox" checked={optIds.includes(o.id)} onChange={() => toggleOpt(o.id)} className="accent-[#F5A623] w-4 h-4" />
                      {o.label}
                    </span>
                    <span className="font-display text-[#F5A623] whitespace-nowrap">+ {formatEuro(o.price)}</span>
                  </label>
                ))}
              </div>
            </section>

            <section data-testid="cfg-customer-section">
              <SectionTitle num="04" title="Vos coordonnées" icon={User} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Nom *" value={customer.nom} onChange={setC("nom")} className={inputCls} data-testid="cfg-nom" />
                <input required placeholder="Prénom *" value={customer.prenom} onChange={setC("prenom")} className={inputCls} data-testid="cfg-prenom" />
                <input required placeholder="Téléphone *" value={customer.telephone} onChange={setC("telephone")} className={inputCls} data-testid="cfg-telephone" />
                <input required type="email" placeholder="E-mail *" value={customer.email} onChange={setC("email")} className={inputCls} data-testid="cfg-email" />
                <input required placeholder="Adresse *" value={customer.adresse} onChange={setC("adresse")} className={`${inputCls} sm:col-span-2`} data-testid="cfg-adresse" />
                <input required placeholder="Code postal *" value={customer.code_postal} onChange={setC("code_postal")} className={inputCls} data-testid="cfg-code-postal" />
                <input required placeholder="Ville *" value={customer.ville} onChange={setC("ville")} className={inputCls} data-testid="cfg-ville" />
              </div>
            </section>
          </div>

          <div>
            <div className="lg:sticky lg:top-24 bg-[#343A3D] border border-[#6EB000]/40 glow-green p-6 sm:p-8" data-testid="cfg-recap">
              <p className="uppercase tracking-widest text-xs text-white/40 mb-6">Récapitulatif</p>
              <div className="flex justify-between text-sm gap-4">
                <span className="text-white/80">{prestation ? prestation.label : "Prestation principale"}</span>
                <span className="text-white font-bold whitespace-nowrap" data-testid="cfg-recap-prestation-price">
                  {prestation ? priceLabel(prestation) : "—"}
                </span>
              </div>
              <div className="mt-4 space-y-2" data-testid="cfg-recap-options">
                {opts.length === 0 ? (
                  <p className="text-white/30 text-sm">Aucune option</p>
                ) : (
                  opts.map((o) => (
                    <div key={o.id} className="flex justify-between text-sm">
                      <span className="text-white/50">+ {o.label}</span>
                      <span className="text-[#F5A623]">{formatEuro(o.price)}</span>
                    </div>
                  ))
                )}
              </div>
              <div className="border-t border-[#454C50] mt-5 pt-5 flex justify-between items-center">
                <span className="uppercase tracking-widest text-xs text-white/50">Total</span>
                <span className="font-display text-4xl text-[#6EB000]" data-testid="cfg-total">
                  {total !== null ? formatEuro(total) : prestation ? "Sur devis" : "—"}
                </span>
              </div>
              {(needsConfirm || (prestation && prestation.price === null)) && (
                <p className="font-script text-lg text-[#F5A623] mt-4" data-testid="cfg-confirm-note">
                  * Confirmation NBPerf nécessaire avant paiement (selon véhicule / fourchette tarifaire)
                </p>
              )}

              <label className="flex items-start gap-3 mt-8 cursor-pointer" data-testid="cfg-cgv-label">
                <input type="checkbox" checked={cgv} onChange={(e) => setCgv(e.target.checked)} className="accent-[#6EB000] w-4 h-4 mt-0.5" data-testid="cfg-cgv-checkbox" />
                <span className="text-xs text-white/60 leading-relaxed">
                  Je confirme que les informations concernant mon véhicule sont exactes et j'accepte les conditions générales de vente.
                </span>
              </label>

              <button
                onClick={submit}
                disabled={busy}
                className="clip-angle mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#6EB000] text-black font-bold px-8 py-5 text-sm uppercase tracking-widest hover:bg-[#5A9000] active:scale-95 transition-all duration-200 disabled:opacity-50"
                data-testid="cfg-submit-button"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {total !== null ? "Valider et payer" : "Envoyer ma demande"}
              </button>
              {total === null && prestation && (
                <p className="text-[11px] text-white/40 mt-3 text-center">
                  Paiement en ligne indisponible pour cette prestation : NBPerf confirme d'abord le tarif exact.
                </p>
              )}
              <p className="flex items-center justify-center gap-2 text-[11px] text-white/40 mt-4">
                <ShieldCheck className="w-4 h-4 text-[#6EB000]" /> PayPal, carte bancaire ou espèces sur place
              </p>
            </div>
          </div>
        </div>
      </div>

      {step === "form" && (
        <button
          type="button"
          onClick={() => document.querySelector('[data-testid="cfg-recap"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#1E2224]/95 backdrop-blur border-t-2 border-[#6EB000]/50 px-5 py-3 flex items-center justify-between gap-4 active:bg-[#272B2E] transition-colors"
          data-testid="mobile-sticky-recap"
        >
          <span className="text-left min-w-0">
            <span className="block text-[10px] uppercase tracking-widest text-white/40">Votre devis</span>
            <span className="block text-sm text-white truncate" data-testid="mobile-sticky-label">
              {prestation ? prestation.label : "Composez votre prestation"}
            </span>
          </span>
          <span className="flex items-center gap-3 shrink-0">
            <span className="font-display text-2xl text-[#6EB000]" data-testid="mobile-sticky-total">
              {total !== null ? formatEuro(total) : prestation ? "Sur devis" : "—"}
            </span>
            <ChevronUp className="w-5 h-5 text-[#6EB000]" />
          </span>
        </button>
      )}
    </main>
  );
}
